import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import {
  getConvsArchiveData,
  getApiReadyFilterOptions,
  getUserConvsArchiveDataFromApi,
  getUsersConvsExcelExportPayload,
  getApiPayloadForExcelExport,
} from './convsArchive.adapter';
import { handleErrors, getNotifMsg } from 'utils';
import {
  handleExportConvsList,
  handleFetchConvsArchive,
  handleFetchUserDetailsConvs,
  handleOpenConversation,
  handleUpdateUserAbout,
  handleExportChatsList,
} from './convsArchive.helpers';
import Types from './convsArchive.types';
import {
  setConvsArchiveList,
  setUserConvsArchiveDetails,
} from './convsArchive.actions';

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

export function* fetchConvsArchive({ payload }) {
  const { data, setLoading } = payload;
  const { locale } = yield select(mapState);

  try {
    const apiPayload = getApiReadyFilterOptions(data);
    const res = yield handleFetchConvsArchive(apiPayload);
    if (res.success) {
      const convertedConvsList = getConvsArchiveData(res.data);
      yield put(setConvsArchiveList(convertedConvsList));
      return;
    }

    if (!res.success && res.status === 'invalid filter type') {
      toast.error(getNotifMsg('SENT_REQUEST_NOT_CORRECT', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading && setLoading(false);
  }
}

export function* onFetchConvsArchive() {
  yield takeLatest(Types.FETCH_CONVS_ARCHIVE_LIST, fetchConvsArchive);
}

export function* fetchUserConvsArchive({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const convId = payload;
    const res = yield handleFetchUserDetailsConvs({ conversationId: convId });
    const convertedData = getUserConvsArchiveDataFromApi(res);
    yield put(setUserConvsArchiveDetails(convertedData));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchUserConvsArchiveDetails() {
  yield takeLatest(
    Types.FETCH_USER_CONVS_ARCHIVE_DETAILS,
    fetchUserConvsArchive
  );
}

export function* openConversation({ payload }) {
  const { locale } = yield select(mapState);
  try {
    const conversationId = payload;
    const res = yield handleOpenConversation({ conversationId });
    if (res.success) {
      toast.success(getNotifMsg('CONVS_ARCHIVE_OPEN_CONV_SUCCESS', locale));
      return;
    }

    if (!res.success && res.msg === 'conversation already assigned to you')
      toast.info(getNotifMsg('CONVS_ARCHIVE_ALREADY_ASSIGNED', locale));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onOpenConversation() {
  yield takeLatest(Types.OPEN_CONVERSATION_FROM_ARCHIVE, openConversation);
}

export function* updateUserAbout({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const { userId, about } = payload;
    const res = yield handleUpdateUserAbout({ userId, about });
    if (res.success) {
      toast.success(getNotifMsg('CONVS_ARCHIVE_INFO_SAVED', locale));
      return;
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onUpdateUserAbout() {
  yield takeLatest(Types.UPDATE_USER_ABOUT, updateUserAbout);
}

export function* exportConvsList({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const data = getUsersConvsExcelExportPayload(payload);

    const res = yield handleExportConvsList(data);

    if (
      res.message ===
      'start to exporting, we`ll email you the link as soon as it get ready'
    ) {
      toast.success(getNotifMsg('CONVS_ARCHIVE_EMAIL_WHEN_READY', locale));
    }
  } catch (error) {
    if (
      error.response?.data?.message === 'you can only export 2 times in day'
    ) {
      toast.error(getNotifMsg('CONVS_ARCHIVE_EXPORT_LIMIT', locale));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* onExportConvsList() {
  yield takeLatest(Types.EXPORT_CONVS_LIST, exportConvsList);
}

export function* exportExcelChatsList({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const apiPayloadForExcelExport = getApiPayloadForExcelExport(payload);
    const data = yield handleExportChatsList(apiPayloadForExcelExport);

    if (data.message === 'please wait') {
      toast.success(getNotifMsg('CONVS_ARCHIVE_EMAIL_WHEN_READY', locale));
    }
  } catch (error) {
    if (error.response?.data?.message === 'limit export') {
      toast.error(getNotifMsg('CONVS_ARCHIVE_EXPORT_LIMIT', locale));
      return;
    } else if (error.response?.data?.message === 'channel not found') {
      toast.error(getNotifMsg('SERVER_ERROR', locale));
      return;
    } else if (error.response?.data?.message === 'upgrade package') {
      toast.error(getNotifMsg('UPGRADE_PACKAGE', locale));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* onExportExcelList() {
  yield takeLatest(Types.EXPORT_EXCEL_BASED_ON_PACKAGE, exportExcelChatsList);
}

export default function* convsArchiveSagas() {
  yield all([
    call(onFetchConvsArchive),
    call(onFetchUserConvsArchiveDetails),
    call(onOpenConversation),
    call(onUpdateUserAbout),
    call(onExportConvsList),
    call(onExportExcelList),
  ]);
}
